import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Home, Mail, ArrowLeft, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.detail || t('errors.generic', 'Something went wrong'));
      }
    } catch (err) {
      setError(t('errors.network', 'Network error. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 bg-signal-blue rounded-sm flex items-center justify-center">
            <Home className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading text-2xl font-bold text-slate-800">
            TLA<span className="text-military-gold">HOUSING</span>
          </span>
        </Link>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          {submitted ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-800 mb-2">
                {t('auth.forgot_check_email_title', 'CHECK YOUR EMAIL')}
              </h1>
              <p className="text-slate-600 text-sm mb-6">
                {t(
                  'auth.forgot_check_email_body',
                  'If an account exists for {email}, you will receive a password reset link shortly.',
                ).replace('{email}', email)}
              </p>
              <p className="text-slate-500 text-xs mb-6">
                {t('auth.forgot_spam_hint', "Don't see it? Check your spam folder or try again.")}
              </p>
              <Link to="/login">
                <Button className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white">
                  {t('auth.forgot_back_login', 'Back to Login')}
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h1 className="font-heading text-2xl font-bold text-slate-800 mb-2">
                  {t('auth.forgot_title', 'FORGOT PASSWORD?')}
                </h1>
                <p className="text-slate-600 text-sm">
                  {t(
                    'auth.forgot_subtitle',
                    "No worries! Enter your email and we'll send you a reset link.",
                  )}
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-sm mb-6">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-slate-700 text-sm font-medium">
                    {t('auth.email_address', 'Email Address')}
                  </Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="pl-10 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-signal-blue"
                      data-testid="forgot-email"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white rounded-sm py-5"
                  data-testid="forgot-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('auth.forgot_sending', 'Sending...')}
                    </>
                  ) : (
                    t('auth.forgot_send_link', 'Send Reset Link')
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-signal-blue"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('auth.forgot_back_login', 'Back to Login')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
